'use client';

import { useEffect, useRef, useState } from 'react';
import styles from './LandingHome.module.css';
import { HERO_VIDEO_DITHERED, HERO_VIDEO_RAW, type HeroVideoKind } from './heroVariants';
import { prefersReducedMotion } from './landingData';

const PX = 3; // dither cell size in canvas pixels

const FRAGMENT = `#version 300 es
precision highp float; out vec4 o;
uniform sampler2D tex; uniform vec2 res; uniform float px; uniform float cover;
float bayer(vec2 c){ ivec2 p = ivec2(mod(c, 8.)); int x = p.x, y = p.y, xy = x ^ y;
  int v = ((xy & 1) << 5) | ((x & 1) << 4) | ((xy & 2) << 2) | ((x & 2) << 1) | ((xy & 4) >> 1) | ((x & 4) >> 2);
  return float(v) / 64.; }
void main(){
  vec2 cell = floor(gl_FragCoord.xy / px);
  vec2 q = (cell * px + px * .5) / res;
  vec2 uv = vec2(q.x, 1. - q.y);
  uv = (uv - .5) * vec2(min(1., cover), min(1., 1. / cover)) + .5;
  vec3 c = texture(tex, uv).rgb;
  float lum = dot(c, vec3(.299, .587, .114));
  float v = clamp((lum - .08) * 1.35, 0., 1.) * .95;
  v *= 1. - .45 * smoothstep(.4, 1.1, length(q - .5) * 1.4);
  float d = bayer(cell);
  vec3 back = vec3(.035, .006, .01), mid = vec3(.80, 0., .2), hi = vec3(1., .86, .84);
  o = vec4(v > d ? (v > .78 + d * .3 ? hi : mid) : back, 1.);
}`;
const VERTEX = `#version 300 es
in vec2 p; void main(){ gl_Position = vec4(p, 0., 1.); }`;

/**
 * Campus flyover footage. A pre-dithered clip plays as-is; a raw clip is dithered live in WebGL2.
 * With no file in public/landing/hero/ it shows where to put one.
 */
export default function VideoBackdrop({ kind, onFail }: { kind: HeroVideoKind; onFail: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [noGl, setNoGl] = useState(false);

  // Raw footage → live dither.
  useEffect(() => {
    if (kind !== 'raw') return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const gl = canvas.getContext('webgl2', { antialias: false, alpha: false, powerPreference: 'low-power' });
    if (!gl) { setNoGl(true); return; }
    let raf = 0, visible = true, disposed = false;
    try {
      const sh = (type: number, src: string) => { const s = gl.createShader(type)!; gl.shaderSource(s, src); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? 'shader'); return s; };
      const prog = gl.createProgram()!;
      gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERTEX));
      gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FRAGMENT));
      gl.linkProgram(prog);
      gl.useProgram(prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, 'p');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      const uRes = gl.getUniformLocation(prog, 'res'), uPx = gl.getUniformLocation(prog, 'px'), uCover = gl.getUniformLocation(prog, 'cover');
      const resize = () => {
        const scale = Math.min(1, window.devicePixelRatio) * 0.75;
        canvas.width = Math.max(1, Math.round(canvas.clientWidth * scale));
        canvas.height = Math.max(1, Math.round(canvas.clientHeight * scale));
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.uniform2f(uRes, canvas.width, canvas.height);
        gl.uniform1f(uPx, Math.max(2, Math.round(canvas.width / 440)) * (PX / 3));
      };
      resize();
      window.addEventListener('resize', resize);
      const frame = () => {
        raf = 0;
        if (disposed || !visible || document.hidden) return;
        if (video.readyState >= 2) {
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
          const va = (video.videoWidth || 16) / (video.videoHeight || 9);
          gl.uniform1f(uCover, (canvas.width / canvas.height) / va);
          gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
        raf = requestAnimationFrame(frame);
      };
      const start = () => { if (!raf) raf = requestAnimationFrame(frame); };
      start();
      const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; start(); });
      io.observe(canvas);
      document.addEventListener('visibilitychange', start);
      return () => { disposed = true; if (raf) cancelAnimationFrame(raf); window.removeEventListener('resize', resize); io.disconnect(); document.removeEventListener('visibilitychange', start); gl.getExtension('WEBGL_lose_context')?.loseContext(); };
    } catch {
      setNoGl(true);
    }
  }, [kind]);

  useEffect(() => {
    const v = videoRef.current;
    if (v && prefersReducedMotion()) v.pause();
  }, [kind]);

  if (!kind) {
    return (
      <p className={styles.videoNotice}>
        Flyover video not found. Add <code>campus-flyover-dithered.mp4</code> (or a raw <code>campus-flyover.mp4</code>) to <code>public/landing/hero/</code>.
      </p>
    );
  }
  const src = kind === 'dithered' ? HERO_VIDEO_DITHERED : HERO_VIDEO_RAW;
  const live = kind === 'raw' && !noGl;
  return (
    <>
      {/* The raw clip stays mounted but hidden: it is only the texture source for the live dither. */}
      <video
        ref={videoRef}
        className={live ? styles.videoSource : `${styles.canvas} ${styles.videoCover} ${kind === 'raw' ? styles.videoTinted : ''}`}
        src={src}
        autoPlay muted loop playsInline preload="auto"
        aria-hidden="true"
        onError={onFail}
      />
      {live ? <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" /> : null}
    </>
  );
}
